import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar,
  Clock,
  DollarSign,
  MapPin,
  RefreshCw,
  TrendingUp,
  User,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';

interface UserSpendingPatternData {
  id: string;
  patternType: 'peak_hours' | 'peak_days' | 'seasonal' | 'recurring';
  patternData: any;
  confidence: number;
  frequency: number;
  lastOccurrence?: Date;
}

interface SpendingPatternsCardProps {
  pattern: UserSpendingPatternData;
}

export function SpendingPatternsCard({ pattern }: SpendingPatternsCardProps) {
  const getPatternIcon = () => {
    switch (pattern.patternType) {
      case 'peak_hours':
        return <Clock className="h-5 w-5" />;
      case 'peak_days':
        return <Calendar className="h-5 w-5" />;
      case 'seasonal':
        return <TrendingUp className="h-5 w-5" />;
      case 'recurring':
        return <RefreshCw className="h-5 w-5" />;
      default:
        return <User className="h-5 w-5" />;
    }
  };

  const getPatternTitle = () => {
    switch (pattern.patternType) {
      case 'peak_hours':
        return 'Horários de Pico';
      case 'peak_days':
        return 'Dias de Maior Gasto';
      case 'seasonal':
        return 'Padrão Sazonal';
      case 'recurring':
        return 'Gasto Recorrente';
      default:
        return 'Padrão Detectado';
    }
  };

  const getPatternDescription = () => {
    switch (pattern.patternType) {
      case 'peak_hours':
        return 'Horários em que você mais gasta';
      case 'peak_days':
        return 'Dias da semana com mais gastos';
      case 'seasonal':
        return 'Variações sazonais nos gastos';
      case 'recurring':
        return 'Gastos que se repetem regularmente';
      default:
        return 'Comportamento identificado';
    }
  };

  const getConfidenceColor = () => {
    if (pattern.confidence >= 0.8) return 'text-green-600';
    if (pattern.confidence >= 0.6) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getConfidenceLabel = () => {
    if (pattern.confidence >= 0.8) return 'Alta';
    if (pattern.confidence >= 0.6) return 'Média';
    return 'Baixa';
  };

  const renderPatternDetails = () => {
    switch (pattern.patternType) {
      case 'peak_hours':
        return (
          <div className="space-y-3">
            <div className="text-muted-foreground text-sm">
              Horários com mais gastos:
            </div>
            <div className="space-y-2">
              {pattern.patternData.hours
                ?.slice(0, 3)
                .map((hour: any, index: number) => (
                  <div
                    className="flex items-center justify-between"
                    key={index}
                  >
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{hour.hour}:00</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-sm">
                        {hour.count} transações
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {hour.percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );

      case 'peak_days':
        return (
          <div className="space-y-3">
            <div className="text-muted-foreground text-sm">
              Dias com mais gastos:
            </div>
            <div className="space-y-2">
              {pattern.patternData.days
                ?.slice(0, 2)
                .map((day: any, index: number) => (
                  <div
                    className="flex items-center justify-between"
                    key={index}
                  >
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{day.day}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-sm">
                        {day.count} transações
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {day.percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        );

      case 'seasonal':
        return (
          <div className="space-y-3">
            <div className="text-muted-foreground text-sm">
              Meses com gastos elevados:
            </div>
            <div className="space-y-2">
              {pattern.patternData.highSpendingMonths
                ?.slice(0, 3)
                .map((month: any, index: number) => (
                  <div
                    className="flex items-center justify-between"
                    key={index}
                  >
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {format(new Date(2024, month.month - 1, 1), 'MMMM', {
                          locale: ptBR,
                        })}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-sm">
                        R${' '}
                        {month.amount.toLocaleString('pt-BR', {
                          minimumFractionDigits: 0,
                        })}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        +{month.percentAboveAverage.toFixed(1)}% vs média
                      </div>
                    </div>
                  </div>
                ))}
            </div>
            <div className="text-muted-foreground text-xs">
              Média mensal: R${' '}
              {pattern.patternData.averageMonthlySpending?.toLocaleString(
                'pt-BR',
                {
                  minimumFractionDigits: 0,
                }
              )}
            </div>
          </div>
        );

      case 'recurring':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Valor</div>
                <div className="font-medium">
                  R${' '}
                  {pattern.patternData.amount?.toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Intervalo</div>
                <div className="font-medium">
                  {pattern.patternData.intervalDays} dias
                </div>
              </div>
            </div>
            <div className="text-muted-foreground text-xs">
              {pattern.patternData.occurrences} ocorrências detectadas
            </div>
          </div>
        );

      default:
        return (
          <div className="text-muted-foreground text-sm">
            Dados do padrão não disponíveis
          </div>
        );
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {getPatternIcon()}
            <CardTitle className="text-base">{getPatternTitle()}</CardTitle>
          </div>
          <Badge variant="secondary">{getConfidenceLabel()}</Badge>
        </div>
        <p className="text-muted-foreground text-sm">
          {getPatternDescription()}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Confidence Score */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Confiança</span>
            <span className={getConfidenceColor()}>
              {(pattern.confidence * 100).toFixed(0)}%
            </span>
          </div>
          <Progress className="h-2" value={pattern.confidence * 100} />
        </div>

        {/* Pattern Details */}
        {renderPatternDetails()}

        {/* Metadata */}
        <div className="border-t pt-3">
          <div className="grid grid-cols-2 gap-4 text-muted-foreground text-xs">
            <div>
              <div>Frequência</div>
              <div className="font-medium text-foreground">
                {pattern.frequency} vezes
              </div>
            </div>
            <div>
              <div>Última ocorrência</div>
              <div className="font-medium text-foreground">
                {pattern.lastOccurrence
                  ? format(new Date(pattern.lastOccurrence), 'dd/MM/yyyy', {
                      locale: ptBR,
                    })
                  : '-'}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
